(function () {

  /* ---------------------------------------------------------------
     SDK ACCESSORS
  --------------------------------------------------------------- */
  function getSDK() {
    return window.WishListHero_SDK;
  }

  function getItems() {
    return getSDK()?.GetWishListItems?.() || [];
  }

  function isActive(variantId) {
    return getItems().some(i => i.ProductVariantId == variantId);
  }

  /* ---------------------------------------------------------------
     HASH RESOLUTION  (the guest fix)

     GetWishListHash()  -> returns an EXISTING hash or null. Never creates.
                           This is why logged-out shoppers silently failed:
                           no hash exists yet, so every add was rejected.
     GetWishListHash(1) -> CreateIfNotFound. Returns the existing hash
                           synchronously, OR a Promise that resolves once
                           WishListHero has created one server-side and
                           dispatched EVENTS_WISHLIST_HASH_GENERATED.

     We try the cheap synchronous read first; only if there's no hash do we
     ask the SDK to mint one. If creation never resolves (e.g. the store has
     guest wishlists disabled and the backend refuses), the SDK's Promise
     hangs forever — so we race it against a timeout and fail loudly.
  --------------------------------------------------------------- */
  const HASH_CREATE_TIMEOUT_MS = 4000;

  async function ensureHash() {
    const sdk = getSDK();
    if (!sdk) return null;

    /* fast path: an existing hash (logged-in, or a returning guest) */
    const existing = sdk.GetWishListHash();
    if (existing) return existing;

    /* no hash yet → create one on demand (guest first-add) */
    try {
      const created = await Promise.race([
        Promise.resolve(sdk.GetWishListHash(1)), // wraps value OR Promise
        new Promise(res => setTimeout(() => res(null), HASH_CREATE_TIMEOUT_MS))
      ]);
      return created || null;
    } catch (err) {
      console.error('[wishlist] hash creation threw', err);
      return null;
    }
  }

  /* ---------------------------------------------------------------
     UI HELPERS
  --------------------------------------------------------------- */
  function updateUI(btn) {
    const active = isActive(btn.dataset.variantId);
    btn.classList.toggle('is-active', active);
  }

  function updateWishlistDot() {
    const count = getItems().length;
    document.querySelectorAll('.black-dot').forEach(dot => {
      dot.classList.toggle('hidden', count === 0);
    });
  }

  /* set is-active on every button sharing this variant */
  function setActiveForVariant(variantId, active) {
    document.querySelectorAll('.maison-add-to-wishlist').forEach(b => {
      if (b.dataset.variantId == variantId) {
        b.classList.toggle('is-active', active);
      }
    });
  }

  /* re-sync all buttons from SDK state (external changes only) */
  function refreshAllButtons() {
    document.querySelectorAll('.maison-add-to-wishlist').forEach(updateUI);
  }

  /* ---------------------------------------------------------------
     RESULT GUARD
     WishListHero RESOLVES (not rejects) on a 400, e.g.
     { ok: false, status: 400, error: ['... WishlistHash ... null'] }.
     Trusting a bare .then() flips the UI to active on a failed write.
     This returns true only on a genuine success.
  --------------------------------------------------------------- */
  function wroteOK(r, label) {
    if (!r || r.ok === false) {
      console.error('[wishlist] SDK rejected ' + label, r && r.error ? r.error : r);
      return false;
    }
    return true;
  }

  /* ---------------------------------------------------------------
     OPTIONAL: handle a guest whose store disallows guest wishlists.
     If ensureHash() came back null AND there's no customer, the right
     UX is to send them to login rather than fail silently. OpenWishList
     already routes to the login popup when DisableGuestCustomer is set.
  --------------------------------------------------------------- */
  function promptLoginIfNeeded() {
    const sdk = getSDK();
    const customerId = sdk?.GetCustomerId?.();
    if (!customerId) {
      /* Opens login popup when guest wishlists are disabled; otherwise
         opens the (empty) wishlist. Comment out if you prefer a no-op. */
      sdk?.OpenWishList?.();
    }
  }

  /* ---------------------------------------------------------------
     TOGGLE  (now async — it may need to create a hash first)
  --------------------------------------------------------------- */
  async function toggle(btn) {
    const sdk = getSDK();
    if (!sdk) {
      console.error('[wishlist] SDK missing on click — embed not loaded.');
      return;
    }

    const variantId = btn.dataset.variantId;
    if (!variantId) {
      console.warn('[wishlist] button has no data-variant-id; cannot toggle.', btn);
      return;
    }

    /* Resolve (and if necessary create) a hash. This is the guest fix. */
    const hash = await ensureHash();
    if (!hash) {
      console.warn(
        '[wishlist] could not obtain a wishlist hash. Likely causes: ' +
        'guest wishlists disabled for this store, cookie-consent blocking, ' +
        'or the embed session never established.'
      );
      promptLoginIfNeeded();
      return;
    }

    if (isActive(variantId)) {

      sdk.DeleteWishListItem({
        ProductVariantId: variantId,
        WishlistHash: hash
      }).then(r => {
        if (!wroteOK(r, 'delete')) return;
        setActiveForVariant(variantId, false);
        updateWishlistDot();
        requestAnimationFrame(() => setActiveForVariant(variantId, false));
      }).catch(err => {
        console.error('[wishlist] delete threw', err);
      });

    } else {

      sdk.AddWishListItem({
        ProductId: btn.dataset.productId,
        ProductVariantId: btn.dataset.variantId,
        ProductTitle: btn.dataset.title,
        ProductPrice: btn.dataset.price,
        ProductLink: btn.dataset.link,
        ProductImage: btn.dataset.image,
        WishlistHash: hash
      }).then(r => {
        if (!wroteOK(r, 'add')) return;
        setActiveForVariant(variantId, true);
        updateWishlistDot();
        requestAnimationFrame(() => setActiveForVariant(variantId, true));
      }).catch(err => {
        console.error('[wishlist] add threw', err);
      });

    }
  }

  /* ---------------------------------------------------------------
     INIT
  --------------------------------------------------------------- */
  function init() {
    const buttons = document.querySelectorAll('.maison-add-to-wishlist');

    buttons.forEach(btn => {
      updateUI(btn);
      btn.removeEventListener('click', btn._wishlistHandler);
      btn._wishlistHandler = () => { toggle(btn); }; // fire-and-forget async
      btn.addEventListener('click', btn._wishlistHandler);
    });

    updateWishlistDot();
  }

  /* ---------------------------------------------------------------
     SDK WAIT — loud on timeout so a missing embed announces itself
  --------------------------------------------------------------- */
  function waitForSDK(callback, tries = 0) {
    const sdk = window.WishListHero_SDK;

    if (
      sdk &&
      typeof sdk.GetWishListItems === 'function' &&
      typeof sdk.GetWishListHash === 'function'
    ) {
      callback();
      return;
    }

    if (tries > 40) { /* ~4s; SDK can be slow on cold loads */
      console.error(
        '[wishlist] WishListHero_SDK never became available after ~4s — ' +
        'embed not loading? Buttons will not be wired.'
      );
      return;
    }

    setTimeout(() => waitForSDK(callback, tries + 1), 100);
  }

  /* ---------------------------------------------------------------
     DOT POLL — gated on visibility instead of running forever.
     The events below cover normal updates; this only catches SDK
     count lag and is paused while the tab is hidden.
  --------------------------------------------------------------- */
  let dotPoll = null;
  function startPoll() {
    if (!dotPoll) dotPoll = setInterval(updateWishlistDot, 2000);
  }
  function stopPoll() {
    if (dotPoll) { clearInterval(dotPoll); dotPoll = null; }
  }

  /* ---------------------------------------------------------------
     LIFECYCLE
  --------------------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', () => {
    waitForSDK(init);
    startPoll();
  });

  /* Theme-editor section reload */
  document.addEventListener('shopify:section:load', () => {
    init();
  });

  /* View transitions / tab return */
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stopPoll();
    } else {
      updateWishlistDot();
      refreshAllButtons();
      startPoll();
    }
  });

  /* Global sync (external changes, e.g. wishlist drawer) */
  document.addEventListener('wishlist-changed', () => {
    updateWishlistDot();
    refreshAllButtons();
  });

  window.addEventListener('pagehide', stopPoll);

})();


/* -----------------------------------------------------------------
   OPEN WISHLIST
----------------------------------------------------------------- */
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('.maison-open-wishlist-btn');
  if (!trigger) return;

  const sdk = window.WishListHero_SDK;
  if (!sdk) return;

  sdk.OpenWishList();
});